/**
 * Helpers must never throw on an unexpected argument type. Conductor validates
 * a switch by *evaluating* its expression when the definition is registered,
 * before any task has run — so every argument arrives as its literal
 * placeholder string and a throw fails the save with a 400.
 *
 *   register: sameAssetIds("${task.output.x}", "${task.output.y}")  <- strings
 *   runtime:  sameAssetIds([{id: 'a'}],       [{id: 'a'}])          <- arrays
 *
 * Guard the shape (`Array.isArray`, `&&` before a property read) rather than
 * relying on `||` fallbacks, which pass a truthy string straight through.
 */
type UnaryRuntimeHelper = ((arg: string) => string) & { declaration: string }
type BinaryRuntimeHelper = ((arg1: string, arg2: string) => string) & {
  declaration: string
}
type TernaryRuntimeHelper = ((
  arg1: string,
  arg2: string,
  arg3: string
) => string) & { declaration: string }

const helperName = (declaration: string): string => {
  const match = declaration.match(/^function\s+(\w+)/)
  if (!match) throw new Error('declaration must be a named function')
  return match[1]
}

const createUnaryRuntimeHelper = (declaration: string): UnaryRuntimeHelper => {
  const name = helperName(declaration)
  return Object.assign((arg: string) => `${name}(${arg})`, { declaration })
}

const createBinaryRuntimeHelper = (
  declaration: string
): BinaryRuntimeHelper => {
  const name = helperName(declaration)
  return Object.assign(
    (arg1: string, arg2: string) => `${name}(${arg1}, ${arg2})`,
    { declaration }
  )
}

const createTernaryRuntimeHelper = (
  declaration: string
): TernaryRuntimeHelper => {
  const name = helperName(declaration)
  return Object.assign(
    (arg1: string, arg2: string, arg3: string) =>
      `${name}(${arg1}, ${arg2}, ${arg3})`,
    { declaration }
  )
}

export const parseAmount = createUnaryRuntimeHelper(
  `function parseAmount(amount) {
    return Number(Number(amount).toFixed(2));
  }`
)

// Equality describes the product's discount as a whole, so every discount of
// the selected type must match. Range checks deliberately stay any-of: one
// risky discount should be enough to trigger approval.
export const allDiscountsEqual = createTernaryRuntimeHelper(
  `function allDiscountsEqual(discounts, config, currencyMatches) {
    if (!currencyMatches) return false;
    var list = Array.isArray(discounts) ? discounts : [];
    var matching = list.filter(function (d) {
      return d && d.type === config.type;
    });
    return matching.length > 0 && matching.every(function (d) {
      return parseAmount(d.discountValue) === Number(config.amount);
    });
  }`
)

// Returns the percentage that `amount` represents of `total`, rounded to 2dp.
// Returns 0 when `total` is 0 to avoid NaN/Infinity from division by zero.
export const parsePercentageOfAmount = createBinaryRuntimeHelper(
  `function parsePercentageOfAmount(amount, total) {
    var t = Number(total);
    if (t === 0) return 0;
    return Number(((Number(amount) / t) * 100).toFixed(2));
  }`
)

// Converts a duration object ({years, months, days, weeks}) to a day count by
// adding it to a reference date and diffing — mirrors date-fns' differenceInDays(add(ref, d), ref).
// Anchors on the upstream quote's start date when defined; falls back to
// `new Date()` when the start date is missing. Returns 0 for null/undefined
// durations; callers null-guard the property before comparing so an empty
// totalDuration doesn't accidentally satisfy a "shorter than" check.
export const durationToDays = createBinaryRuntimeHelper(
  `function durationToDays(d, startDate) {
    if (!d) return 0;
    var ref = startDate ? new Date(startDate) : new Date();
    var target = new Date(ref.getTime());
    var months = 12 * Number(d.years || 0) + Number(d.months || 0);
    if (months) {
      var day = target.getUTCDate();
      target.setUTCDate(1);
      target.setUTCMonth(target.getUTCMonth() + months);
      var lastDay = new Date(
        Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
      ).getUTCDate();
      target.setUTCDate(Math.min(day, lastDay));
    }
    target.setUTCDate(
      target.getUTCDate() +
        Number(d.days || 0) +
        7 * Number(d.weeks || 0)
    );
    return Math.round((target.getTime() - ref.getTime()) / 86400000);
  }`
)

// Phase names are optional. An absent name matches NOT_SET and the negative
// comparisons because it cannot equal or include the configured value.
export const phasesMatchingName = createBinaryRuntimeHelper(
  `function phasesMatchingName(phases, config) {
    var list = Array.isArray(phases) ? phases : [];
    var operator = config && config.operator;
    return list.filter(function (phase) {
      var name = phase && phase.name;
      var hasName = name !== null && name !== undefined && name !== "";
      if (operator === "NOT_SET") return !hasName;
      if (!hasName) {
        return operator === "IS_NOT_EQUAL" || operator === "EXCLUDES";
      }
      if (operator === "IS_EQUAL") return name === config.name;
      if (operator === "IS_NOT_EQUAL") return name !== config.name;
      if (operator === "INCLUDES") {
        return String(name).indexOf(String(config.name)) !== -1;
      }
      if (operator === "EXCLUDES") {
        return String(name).indexOf(String(config.name)) === -1;
      }
      return false;
    });
  }`
)

// An absent duration is open-ended and behaves as infinity for finite
// comparisons.
export const phasesMatchingDuration = createTernaryRuntimeHelper(
  `function phasesMatchingDuration(phases, condition, startDate) {
    var list = Array.isArray(phases) ? phases : [];
    var operator = condition && condition.operator;
    return list.filter(function (phase) {
      var duration = phase && phase.duration;
      var isOpenEnded = duration === null || duration === undefined;
      if (operator === "IS_OPEN_ENDED") return isOpenEnded;
      if (isOpenEnded) {
        return operator === "LONGER_THAN" || operator === "DOES_NOT_EQUAL";
      }
      var phaseDays = durationToDays(duration, startDate);
      var conditionDays = durationToDays(
        condition && condition.duration,
        startDate
      );
      if (operator === "SHORTER_THAN") return phaseDays < conditionDays;
      if (operator === "LONGER_THAN") return phaseDays > conditionDays;
      if (operator === "EQUALS") return phaseDays === conditionDays;
      if (operator === "DOES_NOT_EQUAL") return phaseDays !== conditionDays;
      return false;
    });
  }`
)

// Rebuilds the quote-level product id list from a narrowed set of phases.
export const productIdsInPhases = createUnaryRuntimeHelper(
  `function productIdsInPhases(phases) {
    var list = Array.isArray(phases) ? phases : [];
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var prices =
        list[i] && Array.isArray(list[i].prices) ? list[i].prices : [];
      for (var j = 0; j < prices.length; j++) {
        var productId = prices[j] && prices[j].productId;
        if (
          productId !== null &&
          productId !== undefined &&
          out.indexOf(productId) === -1
        ) {
          out.push(productId);
        }
      }
    }
    return out;
  }`
)

// Keeps the product-discount map shape while retaining only discounts whose
// phaseId belongs to the narrowed phase set.
export const discountsInPhases = createBinaryRuntimeHelper(
  `function discountsInPhases(productDiscounts, phases) {
    var source =
      productDiscounts &&
      typeof productDiscounts === "object" &&
      !Array.isArray(productDiscounts)
        ? productDiscounts
        : {};
    var list = Array.isArray(phases) ? phases : [];
    var phaseIds = list
      .map(function (phase) {
        return phase && phase.id;
      })
      .filter(function (id) {
        return id !== null && id !== undefined;
      });
    var out = {};
    Object.keys(source).forEach(function (productId) {
      var discounts = Array.isArray(source[productId])
        ? source[productId]
        : [];
      out[productId] = discounts.filter(function (discount) {
        return discount && phaseIds.indexOf(discount.phaseId) !== -1;
      });
    });
    return out;
  }`
)

// Looks up every applicable discount for a product from the upstream
// productDiscounts map, which is keyed by product ID. Returns an empty
// array when the map is missing or the product has no entry.
export const findProductDiscounts = createBinaryRuntimeHelper(
  `function findProductDiscounts(productDiscounts, productId) {
    return (productDiscounts && productDiscounts[productId]) || [];
  }`
)

// Collects every price object across the quote's phases whose
// `productId` matches. Mirrors the get-quote response shape:
// `phases[].prices[]` where each price carries `productId` and the
// usual price fields (`structure`, `name`, `billingFrequency`, etc.).
// Reusable building block for any operator that needs to inspect a
// product's prices — pricing-model lookup, future per-product amount
// operators, etc.
export const findProductPrices = createBinaryRuntimeHelper(
  `function findProductPrices(phases, productId) {
    if (!phases || !phases.length) return [];
    var out = [];
    for (var i = 0; i < phases.length; i++) {
      var prices = (phases[i] && phases[i].prices) || [];
      for (var j = 0; j < prices.length; j++) {
        var price = prices[j];
        if (price && price.productId === productId) out.push(price);
      }
    }
    return out;
  }`
)

// Resolves a product's prices via `findProductPrices` and projects each
// to a `{ pricingType, hasTiers }` entry. `hasTiers` is what lets
// SEAT_BASED callers distinguish linear from graduated.
export const findProductPricingModels = createBinaryRuntimeHelper(
  `function findProductPricingModels(phases, productId) {
    var prices = findProductPrices(phases, productId);
    var out = [];
    for (var i = 0; i < prices.length; i++) {
      var structure = prices[i].structure || {};
      out.push({
        pricingType: structure.pricingType,
        hasTiers: !!(structure.tiers && structure.tiers.length)
      });
    }
    return out;
  }`
)

// Resolves a product's prices via `findProductPrices` and projects each to its
// billing frequency. `billingFrequency` sits on the price itself rather than
// inside `structure`, so this can't reuse `findProductPricingModels`.
export const findProductBillingFrequencies = createBinaryRuntimeHelper(
  `function findProductBillingFrequencies(phases, productId) {
    var prices = findProductPrices(phases, productId);
    var out = [];
    for (var i = 0; i < prices.length; i++) {
      out.push(prices[i].billingFrequency);
    }
    return out;
  }`
)

// Resolves a product's prices via `findProductPrices` and projects each to its
// billing type. Like `billingFrequency`, `billingType` sits on the price itself
// rather than inside `structure`.
export const findProductBillingTypes = createBinaryRuntimeHelper(
  `function findProductBillingTypes(phases, productId) {
    var prices = findProductPrices(phases, productId);
    var out = [];
    for (var i = 0; i < prices.length; i++) {
      out.push(prices[i].billingType);
    }
    return out;
  }`
)

// Compares asset ids ignoring order, but counting repeats. Each step below
// guards a distinct failure mode, hence more than an index-wise compare:
//
//   Array.isArray(v) ? v : []  | registration evaluates with "${...}" strings;
//                              | a throw there fails the save with a 400
//   asset && asset.id          | a malformed entry must not throw mid-expression
//   filter(Boolean)            | drops blanks only — a repeated id is kept,
//                              | since [a, a] and [a] differ materially
//   .sort()                    | attachment order carries no meaning
//   length === ... && every    | without the length check a shorter left side
//                              | passes: [a] vs [a, b] would read as equal
//
//   shape: [{ id, fileName, fileSizeBytes, ... }] on both sides
export const sameAssetIds = createBinaryRuntimeHelper(
  `function sameAssetIds(a, b) {
    function ids(v) {
      return (Array.isArray(v) ? v : [])
        .map(function (asset) {
          return asset && asset.id;
        })
        .filter(Boolean)
        .sort();
    }
    var left = ids(a);
    var right = ids(b);
    return (
      left.length === right.length &&
      left.every(function (id, index) {
        return id === right[index];
      })
    );
  }`
)

// Compares raw text exactly. Both sides come from the same content-editor
// extraction, so only absence is normalised — folding case or inner whitespace
// would let "is equal" ignore real edits.
//
//   shape: string | null on both sides
//
//   null / undefined -> ""      | a text-less template matches an empty quote
//   String(v).trim() | tolerates a stray edge newline, nothing more
//                    | (also absorbs the "${...}" registration-time strings)
export const sameRawText = createBinaryRuntimeHelper(
  `function sameRawText(a, b) {
    function text(v) {
      return v === null || v === undefined ? "" : String(v).trim();
    }
    return text(a) === text(b);
  }`
)

// Compares payment terms as day counts. A quote, a template and an approved
// quote all carry `defaultDueDateDays` the same way — the executors emit it as
// a JSON number, and always emit the key, so inheriting the account default
// comes through as `null`:
//
//   shape: number | null on both sides
//
//   null / undefined / "" | all mean "take the account default", so two unset
//                         | sides are unchanged. Only `null` arrives from an
//                         | executor; the other two are belt and braces.
//   Number(v)             | tolerates a day count that arrived as a string, and
//                         | turns a registration-time "${...}" into NaN rather
//                         | than throwing
//
// An unset side never equals a set one: the account default isn't reachable at
// evaluation time, so 30 explicit days can't be told from 30 inherited.
export const samePaymentTerms = createBinaryRuntimeHelper(
  `function samePaymentTerms(a, b) {
    function days(v) {
      return v === null || v === undefined || v === "" ? null : Number(v);
    }
    return days(a) === days(b);
  }`
)

export const runtimeHelperDeclarations = [
  parseAmount,
  allDiscountsEqual,
  parsePercentageOfAmount,
  durationToDays,
  phasesMatchingName,
  phasesMatchingDuration,
  productIdsInPhases,
  discountsInPhases,
  findProductDiscounts,
  findProductPrices,
  findProductPricingModels,
  findProductBillingFrequencies,
  findProductBillingTypes,
  sameAssetIds,
  sameRawText,
  samePaymentTerms
]
  .map(({ declaration }) => declaration)
  .join(' ')
